/* eslint-disable @typescript-eslint/no-explicit-any */
import { useEffect, useMemo } from 'react';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useClientContext } from 'pos-client/contexts/ClientContext.tsx';
import { trpc } from 'pos-client/utils/trpc.ts';
import type {
  LocationModel,
  LocationSearchRequestModel,
  LocationSummarySearchResponseFeModel,
} from 'pos-shared/models/locationSchema.ts';
import { serviceTypeEnum } from 'pos-shared/models/resourceManagementSchema.ts';
import { time } from 'pos-shared/utils/millisecondsHelper.ts';

type Options = Partial<
  Omit<
    UseQueryOptions<
      LocationSearchRequestModel,
      unknown,
      LocationSummarySearchResponseFeModel,
      any
    >,
    'queryKey' | 'queryFn'
  >
>;
export const useLocations = (
  locationQuery: LocationSearchRequestModel = {},
  options: Options = {},
  useFind = false
) => {
  const response = trpc.locations.findLocations.useQuery(
    {
      pageNumber: 1,
      resultsPerPage: 5000,
      justMyLocations: !useFind,
      ...locationQuery,
    } as LocationSearchRequestModel,
    {
      cacheTime: time.oneDayInMilliseconds,
      staleTime: time.oneDayInMilliseconds,
      refetchOnMount: false,
      refetchOnReconnect: false,
      ...options,
    } as any
  );

  const {
    data,
    isLoading: isLoadingLocations,
    isFetching: isFetchingLocations,
    ...rest
  } = response;

  const locations = ((data as LocationSummarySearchResponseFeModel)?.results ??
    []) as LocationModel[];

  const locationOptions = locations
    .map((o) => ({
      value: o.id,
      label: o.name,
    }))
    .filter(
      (obj, idx, self) => idx === self.findIndex((o) => o.value === obj.value)
    );

  return {
    locations,
    locationSearchResponse: data as LocationSummarySearchResponseFeModel,
    isLoadingLocations,
    isFetchingLocations,
    locationOptions,
    ...rest,
  };
};

export const useLocation = (
  id: string | undefined,
  options: Options = {},
  useFind = true,
  additionalQuery: Partial<LocationSearchRequestModel> = {}
) => {
  const { isLoadingLocations, locationSearchResponse, ...rest } = useLocations(
    { listOfIds: [id].toString(), ...additionalQuery },
    { enabled: !!id, ...options },
    useFind
  );

  const location = useMemo(
    () =>
      locationSearchResponse?.results.find((location) => location.id === id),
    [locationSearchResponse]
  );

  const { handleChangeClient } = useClientContext();

  useEffect(() => {
    if (location) {
      handleChangeClient(location);
    }
  }, [handleChangeClient, location]);

  const serviceTypeOptions = useMemo(
    () =>
      location?.serviceTypes
        ? location.serviceTypes
            .filter((serviceType) => serviceType !== 'None')
            .map((serviceType) => ({ label: serviceType, value: serviceType }))
        : [
            {
              label: serviceTypeEnum.enum.Static,
              value: serviceTypeEnum.enum.Static,
            },
          ],
    [location]
  );

  return {
    location,
    serviceTypeOptions,
    isLoadingLocation: isLoadingLocations,
    ...rest,
  };
};
